import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper';
import 'swiper/swiper-bundle.min.css';
import {
  TimelineContainer,
  TimelineWrapper,
  TimelineLine,
  TimelinePoint,
  TimelineNavButton,
  NavButtonsContainer,
  MobileTimelineWrapper,
  DesktopTimelineWrapper,
  MobileSwiperContainer,
  SwiperYearSlide,
} from './Timeline.css';

const MOBILE_VISIBLE_COUNT = 4;

// Animation timing variables (in ms)
export const TIMELINE_ANIMATION = {
  LINE_DURATION: 600,        // Sequence 1: horizontal line draw
  YEAR_REVEAL_DELAY: 350,    // Delay between each year reveal during initial animation
  STEP_DELAY: 300,           // Delay between each step when navigating years (synced with map)
};

// Calculate total timeline animation duration
export const getTimelineAnimationDuration = (yearCount) => {
  return TIMELINE_ANIMATION.LINE_DURATION + 
         (yearCount * TIMELINE_ANIMATION.YEAR_REVEAL_DELAY);
};

const Timeline = ({ selectedYear, setSelectedYear, projects, onAnimationComplete, onInitialAnimationComplete, skipInitialAnimation }) => {
  const swiperRef = useRef(null);
  const [animatingYear, setAnimatingYear] = useState(null);
  const [initialAnimationDone, setInitialAnimationDone] = useState(skipInitialAnimation || false);
  const [revealedYearIndex, setRevealedYearIndex] = useState(skipInitialAnimation ? 999 : -1); // Track which years have been revealed
  const animationRef = useRef(null);
  const initialAnimationRef = useRef(null);

  // Extract unique years from projects and sort them
  // If current month is December, add next year to the timeline
  const { years, projectYears } = useMemo(() => {
    const yearSet = new Set();
    projects.forEach(({ node }) => {
      if (node?.data?.year) {
        yearSet.add(parseInt(node.data.year, 10));
      }
    });
    const sortedProjectYears = Array.from(yearSet).sort((a, b) => a - b);
    
    // Clone for display years
    const displayYears = [...sortedProjectYears];
    
    // If it's December, add next year to the timeline
    const now = new Date();
    const currentMonth = now.getMonth(); // 0-indexed, December = 11
    const currentYear = now.getFullYear();
    const nextYear = currentYear + 1;

    const latestProjectYear =
      sortedProjectYears.length > 0
        ? sortedProjectYears[sortedProjectYears.length - 1]
        : null;

    // Add a placeholder for next year only when:
    // - it's December
    // - the latest project year is the current year (meaning next year has no projects yet)
    // This prevents adding an extra year when next year's projects already exist.
    if (
      currentMonth === 11 &&
      latestProjectYear === currentYear &&
      !displayYears.includes(nextYear)
    ) {
      displayYears.push(nextYear);
    }
    
    return { years: displayYears, projectYears: sortedProjectYears };
  }, [projects]);

  // Run merged initial animation: bar draws, then reveal years one by one (each becomes selected)
  useEffect(() => {
    if (years.length > 0 && !initialAnimationDone && selectedYear === null) {
      const lastIndex = years.length - 1;
      
      // Start after line animation completes
      const startDelay = TIMELINE_ANIMATION.LINE_DURATION;
      
      setTimeout(() => {
        let currentIndex = 0;
        
        // Reveal first year immediately
        setRevealedYearIndex(0);
        setSelectedYear(years[0]);
        setAnimatingYear(years[0]);
        
        if (lastIndex === 0) {
          setAnimatingYear(null);
          setInitialAnimationDone(true);
          if (onInitialAnimationComplete) {
            onInitialAnimationComplete();
          }
          return;
        }
        
        // Reveal subsequent years one by one
        initialAnimationRef.current = setInterval(() => {
          currentIndex += 1;
          const currentYear = years[currentIndex];
          
          setRevealedYearIndex(currentIndex);
          setSelectedYear(currentYear);
          setAnimatingYear(currentYear);
          
          if (currentIndex >= lastIndex) {
            clearInterval(initialAnimationRef.current);
            initialAnimationRef.current = null;
            setAnimatingYear(null);
            setInitialAnimationDone(true);
            if (onInitialAnimationComplete) {
              onInitialAnimationComplete();
            }
          }
        }, TIMELINE_ANIMATION.YEAR_REVEAL_DELAY);
      }, startDelay);
    }
  }, [years, initialAnimationDone, selectedYear, setSelectedYear, onInitialAnimationComplete]);
  
  // Cleanup initial animation on unmount
  useEffect(() => {
    return () => {
      if (initialAnimationRef.current) {
        clearInterval(initialAnimationRef.current);
      }
    };
  }, []);

  // Slide to keep the revealed/selected year visible
  useEffect(() => {
    if (swiperRef.current && selectedYear) {
      const yearIndex = years.indexOf(selectedYear);
      if (yearIndex !== -1) {
        // During initial animation, slide to keep the current year in view
        // After animation, slide to show the selected year
        const targetSlide = Math.max(0, yearIndex - MOBILE_VISIBLE_COUNT + 1);
        swiperRef.current.slideTo(targetSlide);
      }
    }
  }, [selectedYear, years]);

  // Notify parent when line animation completes (so pins can start appearing with years)
  useEffect(() => {
    if (years.length > 0 && onAnimationComplete) {
      const timer = setTimeout(() => {
        onAnimationComplete();
      }, TIMELINE_ANIMATION.LINE_DURATION);
      return () => clearTimeout(timer);
    }
  }, [years.length, onAnimationComplete]);

  const handleYearClick = useCallback((targetYear) => {
    if (targetYear === selectedYear || animatingYear !== null || !initialAnimationDone) return;
    
    const currentIndex = years.indexOf(selectedYear);
    const targetIndex = years.indexOf(targetYear);
    
    if (currentIndex === -1 || targetIndex === -1) {
      setSelectedYear(targetYear);
      return;
    }
    
    // Clear any existing animation
    if (animationRef.current) {
      clearInterval(animationRef.current);
    }
    
    const direction = targetIndex > currentIndex ? 1 : -1;
    let stepIndex = currentIndex;
    
    // Start stepping through years - update selectedYear at each step to sync with map
    animationRef.current = setInterval(() => {
      stepIndex += direction;
      const stepYear = years[stepIndex];
      setAnimatingYear(stepYear);
      setSelectedYear(stepYear); // Update selected year to trigger map country reveal
      
      if (stepIndex === targetIndex) {
        clearInterval(animationRef.current);
        animationRef.current = null;
        setAnimatingYear(null);
      }
    }, TIMELINE_ANIMATION.STEP_DELAY);
  }, [selectedYear, years, animatingYear, initialAnimationDone, setSelectedYear]);
  
  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        clearInterval(animationRef.current);
      }
    };
  }, []);

  if (years.length === 0) {
    return null;
  }

  // Start at first slide during initial animation, or last visible slides after
  const initialSlideIndex = 0;

  return (
    <TimelineContainer>
      {/* Mobile view - Swiper timeline */}
      <MobileTimelineWrapper>
        <MobileSwiperContainer>
          <Swiper
            modules={[Navigation]}
            spaceBetween={0}
            slidesPerView={MOBILE_VISIBLE_COUNT}
            initialSlide={initialSlideIndex}
            navigation={{
              prevEl: '.swiper-button-prev-custom',
              nextEl: '.swiper-button-next-custom',
            }}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
          >
            {years.map((year, index) => {
              const isActive = animatingYear === year || (animatingYear === null && selectedYear === year);
              const isRevealed = initialAnimationDone || index <= revealedYearIndex;
              return (
                <SwiperSlide key={year}>
                  <SwiperYearSlide
                    onClick={() => handleYearClick(year)}
                    isSelected={isActive}
                    isRevealed={isRevealed}
                    aria-label={`Filter by year ${year}`}
                    aria-pressed={selectedYear === year}
                  >
                    <span className="year-label">{year}</span>
                    <span className="year-dot" />
                  </SwiperYearSlide>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </MobileSwiperContainer>
        <NavButtonsContainer>
          <TimelineNavButton
            className="swiper-button-prev-custom"
            aria-label="Previous years"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </TimelineNavButton>
          <TimelineNavButton
            className="swiper-button-next-custom"
            aria-label="Next years"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </TimelineNavButton>
        </NavButtonsContainer>
      </MobileTimelineWrapper>

      {/* Desktop view - all years visible */}
      <DesktopTimelineWrapper>
        <TimelineWrapper>
          <TimelineLine>
            {years.map((year, index) => {
              const isActive = animatingYear === year || (animatingYear === null && selectedYear === year);
              const isRevealed = initialAnimationDone || index <= revealedYearIndex;
              return (
                <TimelinePoint
                  key={year}
                  onClick={() => handleYearClick(year)}
                  aria-label={`Filter by year ${year}`}
                  aria-pressed={selectedYear === year}
                  data-year={year}
                  isSelected={isActive}
                  isRevealed={isRevealed}
                  animationIndex={index}
                />
              );
            })}
          </TimelineLine>
        </TimelineWrapper>
      </DesktopTimelineWrapper>
    </TimelineContainer>
  );
};

Timeline.propTypes = {
  selectedYear: PropTypes.number,
  setSelectedYear: PropTypes.func.isRequired,
  projects: PropTypes.array.isRequired,
  onAnimationComplete: PropTypes.func,
  onInitialAnimationComplete: PropTypes.func,
  skipInitialAnimation: PropTypes.bool,
};

export default Timeline;
